import * as React from 'react';
import { Minus, Plus } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface NumberFieldProps {
  id?: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  'aria-label'?: string;
}

const clamp = (n: number, min?: number, max?: number) => {
  let v = n;
  if (min !== undefined) v = Math.max(v, min);
  if (max !== undefined) v = Math.min(v, max);
  return v;
};

const decimalsOf = (step: number) => {
  const s = String(step);
  return s.includes('.') ? s.split('.')[1].length : 0;
};

/** Numeric input with +/- steppers. Flat, bordered, no motion — used everywhere the ROI page takes a number. */
export const NumberField = React.forwardRef<HTMLInputElement, NumberFieldProps>(
  ({ id, value, onChange, min, max, step = 1, prefix, suffix, className, ...aria }, ref) => {
    const decimals = decimalsOf(step);
    const round = (n: number) => Number(n.toFixed(decimals));

    const handleStep = (dir: 1 | -1) => onChange(clamp(round(value + dir * step), min, max));

    return (
      <div
        className={cn(
          // max-w keeps a stepper from stretching to the full width of a wide
          // grid cell — a 400px-wide box for a 3-digit number reads as broken.
          'flex h-9 w-full max-w-56 items-stretch overflow-hidden rounded-md border border-input bg-background focus-within:ring-1 focus-within:ring-ring',
          className,
        )}
      >
        <button
          type='button'
          tabIndex={-1}
          onClick={() => handleStep(-1)}
          disabled={min !== undefined && value <= min}
          className='flex w-8 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40'
          aria-label='Decrease'
        >
          <Minus className='size-3.5' />
        </button>
        {prefix && (
          <span className='flex items-center border-l border-input px-1.5 text-sm text-muted-foreground'>{prefix}</span>
        )}
        <input
          ref={ref}
          id={id}
          type='number'
          inputMode='decimal'
          value={Number.isFinite(value) ? value : ''}
          step={step}
          min={min}
          max={max}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onChange(0);
              return;
            }
            const parsed = Number(raw);
            if (Number.isFinite(parsed)) onChange(clamp(parsed, min, max));
          }}
          className='w-full min-w-0 flex-1 border-l border-input bg-transparent px-2 text-center text-sm tabular-nums focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
          {...aria}
        />
        {suffix && (
          <span className='flex items-center border-l border-input px-1.5 text-sm text-muted-foreground'>{suffix}</span>
        )}
        <button
          type='button'
          tabIndex={-1}
          onClick={() => handleStep(1)}
          disabled={max !== undefined && value >= max}
          className='flex w-8 shrink-0 items-center justify-center border-l border-input text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40'
          aria-label='Increase'
        >
          <Plus className='size-3.5' />
        </button>
      </div>
    );
  },
);
NumberField.displayName = 'NumberField';
